import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { omitUndefined, pick } from '@shared/helpers/object.helpers';
import { is } from '@client/helpers/dom.guards';
import { getSelectedElementId, getUIState, useUIState } from '@client/store/ui';
import { addedElementIdAfterPathSync } from './use-router.helpers';
import {
  innerNavigateOptionsFromProjectNavigate,
  resolveProjectNavigation,
  type SearchParamsPatch,
} from './project-navigation.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { stringToPage } from '@client/store/ui/ui.helper';
import {
  type NodonProjectFullUrlParams,
  type NodonProjectListUrlParams,
  type NodonGenerationProjectUrlParams,
  type NodonSharedProjectUrlParams,
  nodonGenerationProjectUrlParamKeys,
  nodonProjectUrlParamKeys,
  nodonSharedProjectUrlParamKeys,
  ProjectSelectionParams,
  URL_CATEGORY_NONE,
  SELECTED_ELEMENT_SEARCH_PARAM,
} from '@client/models/router.model';
import { useSyncQueryParamsMethod } from './use-router-query-params.hooks';
import {
  NODON_PROJECT_PATH,
  NODON_SHARED_PROJECT_MATCH_PATH,
  NODON_GENERATION_PROJECT_MATCH_PATH,
  NODON_NOT_FOUND,
  isReservedProjectRoutePrefix,
} from '@shared/constants/router.constants';
import {
  PROJECT_NOT_FOUND_MESSAGE,
  PROJECT_VERSION_NOT_FOUND_MESSAGE,
} from '@client/helpers/resource-load.helpers';
import { IProjectInfo } from '@shared/models/project.model';
import { SHARED_PROJECT } from '@shared/constants';
import { createLocalStorageRecordStore } from '../helpers/local-storage.helpers';
import { useSetSelectedOrganization } from '@client/store/organization';
import { Page } from '@client/store/ui/ui.model';
import { decodeRoutePathParam } from '@shared/helpers/router.helpers';
import { useObjectMemoDeep } from './use-object.hooks';

const lastOrgStore = createLocalStorageRecordStore<string, string | undefined>(
  'last_org',
  undefined,
);

const orgLSData = {
  get: () => lastOrgStore.getItem('last'),
  set: (org: string) => lastOrgStore.setItem('last', org),
};

const defaultSelectionParams: ProjectSelectionParams = {
  page: Page.Design,
  elementId: URL_CATEGORY_NONE,
};

/** Reads the selected element id (`?selected=`) from a URL search string. */
export const selectedElementIdFromSearch = (
  search: string | undefined,
): string | undefined => {
  if (!search) return undefined;
  return (
    new URLSearchParams(search).get(SELECTED_ELEMENT_SEARCH_PARAM) || undefined
  );
};

type RawProjectParamsData = Pretty<
  Partial<
    Pick<NodonProjectFullUrlParams, 'organization' | 'projectId' | 'versionId'>
  > &
    ProjectSelectionParams
>;

type RawSharedProjectParamsData = Pretty<
  Partial<Pick<NodonSharedProjectUrlParams, 'sharingKey' | 'versionId'>> &
    ProjectSelectionParams
>;

type RawGenerationProjectParamsData = Pretty<
  Partial<
    Pick<NodonGenerationProjectUrlParams, 'generationKey' | 'versionId'>
  > &
    ProjectSelectionParams
>;

export type RawProjectParamsObject =
  | { type: 'project'; data: RawProjectParamsData }
  | { type: 'shared'; data: RawSharedProjectParamsData }
  | { type: 'generation'; data: RawGenerationProjectParamsData };

export type ProjectParamsErrorData = { type: 'error' } & (
  | {
      error: Error;
      action: 'redirect';
      path: typeof NODON_NOT_FOUND;
    }
  | {
      error: Error;
      action: 'wait';
    }
);
export type ProjectParamsError = {
  type: 'error';
  data: ProjectParamsErrorData;
};
export type ProjectParamsObject =
  | { type: 'shared'; data: NodonSharedProjectUrlParams }
  | { type: 'generation'; data: NodonGenerationProjectUrlParams }
  | { type: 'project'; data: NodonProjectFullUrlParams }
  | { type: 'project-list'; data: NodonProjectListUrlParams }
  | ProjectParamsError;

const useSyncParamsMethod = () => {
  const {
    setSelectedVersionId,
    setSelectedPage,
    setSelectedElementId,
    setAddedElementId,
  } = useUIState();
  const setSelectedOrganization = useSetSelectedOrganization();

  return useCallback(
    (params: ProjectParamsObject) => {
      if (params.type === 'error') return;
      // Keep a version-add rename hint only while this URL is that version.
      setAddedElementId(
        addedElementIdAfterPathSync(
          getUIState().addedElementId,
          params.type === 'project' ? params.data.versionId : undefined,
        ),
      );
      switch (params.type) {
        case 'project':
          {
            if (params.data.organization) {
              setSelectedOrganization(params.data.organization);
            }
            setSelectedVersionId(params.data.versionId);
            setSelectedPage(params.data.page);
            setSelectedElementId(params.data.elementId);
          }
          break;
        case 'project-list':
          {
            setSelectedOrganization(params.data.organization);
            setSelectedVersionId(undefined);
            setSelectedPage(defaultSelectionParams.page);
            setSelectedElementId(defaultSelectionParams.elementId);
          }
          break;
        case 'shared':
        case 'generation':
          setSelectedOrganization(SHARED_PROJECT);
          setSelectedVersionId(params.data.versionId);
          setSelectedPage(params.data.page);
          setSelectedElementId(params.data.elementId);
          break;
      }
    },
    [
      setSelectedOrganization,
      setSelectedVersionId,
      setSelectedPage,
      setSelectedElementId,
      setAddedElementId,
    ],
  );
};

/**
 * Syncs URL params (path + query) to the UI state.
 * Values that are synced should ONLY be set via this hook.
 * @param params - The validated path-derived URL params
 */
export const useUrlToUiStoreSync = (
  /** Params to sync to the UI store */
  params: ProjectParamsObject,
): void => {
  const syncPathMethod = useSyncParamsMethod();
  const syncQueryMethod = useSyncQueryParamsMethod();

  const memoParams = useObjectMemoDeep(params);
  // Layout effect so org is SHARED_PROJECT before children fire authenticated API calls.
  useLayoutEffect(() => {
    syncPathMethod(memoParams);
  }, [memoParams, syncPathMethod]);
  // Query sync must re-run when ?proposal= changes (callback identity follows
  // that id) so the remembered proposal stays current. Path sync must not —
  // it clears addedElementId and would drop the last-added highlight.
  useLayoutEffect(() => {
    syncQueryMethod(memoParams);
  }, [memoParams, syncQueryMethod]);
};

/**
 * Returns raw (non-validated) url params for project pages
 * @returns RawProjectParamsObject
 */
export const selectionParamsFromRoute = (
  params: Record<string, string | undefined>,
  /** Current URL search string; `?selected=` takes precedence over the path. */
  search?: string,
): ProjectSelectionParams =>
  ({
    ...defaultSelectionParams,

    ...omitUndefined({
      page: stringToPage(params.page),
      // Prefer the `?selected=` query param; fall back to the legacy `/e/<id>`
      // path segment so old links keep their selection.
      // LEGACY SHIM (DEV-3454, remove after 2027-01-01): drop the
      // `decodeRoutePathParam(params.elementId)` fallback here — see the shim
      // note in shared/constants/router.constants.ts for the full checklist.
      elementId:
        selectedElementIdFromSearch(search) ??
        decodeRoutePathParam(params.elementId),
    }),
  }) as ProjectSelectionParams;

export const useRawProjectParams = (): RawProjectParamsObject => {
  const { pathname, search } = useLocation();
  const routerParams = useParams();

  return useMemo(() => {
    // Parse from pathname first: NODON_PROJECT_PATH also matches /generation/... as organization=generation.
    if (pathname.startsWith('/generation')) {
      const matched = matchPath(
        NODON_GENERATION_PROJECT_MATCH_PATH,
        pathname,
      )?.params;
      if (!matched?.generationKey) {
        return {
          type: 'generation',
          data: {
            generationKey: '',
            versionId: '',
            ...selectionParamsFromRoute({}, search),
            elementId: URL_CATEGORY_NONE,
          },
        };
      }
      const generationParams = {
        type: 'generation' as const,
        data: {
          ...pick(matched, ...nodonGenerationProjectUrlParamKeys),
          ...selectionParamsFromRoute(matched, search),
        },
      };
      return generationParams;
    }

    if (pathname.startsWith('/shared')) {
      const matched = matchPath(
        NODON_SHARED_PROJECT_MATCH_PATH,
        pathname,
      )?.params;
      if (matched?.sharingKey && matched?.versionId) {
        return {
          type: 'shared',
          data: {
            ...pick(matched, ...nodonSharedProjectUrlParamKeys),
            ...selectionParamsFromRoute(matched, search),
          },
        };
      }
    }

    const matched =
      matchPath(NODON_PROJECT_PATH, pathname)?.params ?? routerParams;
    const projectParams = {
      type: 'project' as const,
      data: {
        ...pick(matched, ...nodonProjectUrlParamKeys),
        ...selectionParamsFromRoute(matched, search),
      },
    };
    return projectParams;
  }, [pathname, search, routerParams]);
};

/**
 * Validates shared project params.
 * Returns error object with instructions on how to handle the error
 * @param params - The URL params
 * @returns NodonSharedProjectUrlParams | ProjectParamsErrorData
 */
export const validateSharedProjectParams = (
  params: Partial<NodonSharedProjectUrlParams>,
  fallbackVersionId: string = 'none',
): NodonSharedProjectUrlParams | ProjectParamsErrorData => {
  const { sharingKey, versionId, page, elementId } = params;

  if (!sharingKey || !versionId) {
    return {
      type: 'error',
      error: new Error('Missing required parameters'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }
  // because I spent too long on this, fallbackVersionId can sometimes be empty string, and that completely screws up routing
  const realFallback = fallbackVersionId?.length ? fallbackVersionId : 'none';

  return {
    sharingKey,
    versionId: versionId === 'none' || !versionId ? realFallback : versionId,
    page: stringToPage(page),
    elementId: elementId ?? URL_CATEGORY_NONE,
  };
};

/**
 * Validates time-limited PDF generation project params (same shape as shared links).
 */
export const validateGenerationProjectParams = (
  params: Partial<NodonGenerationProjectUrlParams>,
  fallbackVersionId: string = 'none',
): NodonGenerationProjectUrlParams | ProjectParamsErrorData => {
  const { generationKey, versionId, page, elementId } = params;

  if (!generationKey || !versionId) {
    return {
      type: 'error',
      error: new Error('Missing required parameters'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }
  const realFallback = fallbackVersionId?.length ? fallbackVersionId : 'none';

  return {
    generationKey,
    versionId: versionId === 'none' || !versionId ? realFallback : versionId,
    page: stringToPage(page),
    elementId: elementId ?? URL_CATEGORY_NONE,
  };
};

export const getFallbackOrg = (userOrganizations: string[]) => {
  const lastOrg = orgLSData.get();
  return lastOrg && userOrganizations.includes(lastOrg)
    ? lastOrg
    : userOrganizations[0];
};

/**
 * Validates and automatically completes project params.
 * Returns error objects with instructions on how to handle errors if needed
 * @param params - The URL params
 * @param userOrganizations - The user's organizations
 * @param projectsData - Fetched projects and fetch state (isFetched, optional error when fetch failed)
 * @returns NodonProjectFullUrlParams | NodonProjectListUrlParams | ProjectParamsErrorData
 */
export const validateAndCompleteProjectParams = (
  params: Partial<NodonProjectFullUrlParams>,
  userOrganizations: string[],
  projectsData: {
    isFetched: boolean;
    projects: IProjectInfo[];
    error?: Error | null;
  },
):
  | NodonProjectFullUrlParams
  | NodonProjectListUrlParams
  | ProjectParamsErrorData => {
  const { isFetched, projects, error: projectsError } = projectsData;

  if (isReservedProjectRoutePrefix(params.organization)) {
    return {
      type: 'error',
      error: new Error('Invalid URL'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  if (userOrganizations.length === 0) {
    return {
      type: 'error',
      error: new Error('You are not a member of any organization'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  if (params.organization && !userOrganizations.includes(params.organization)) {
    return {
      type: 'error',
      error: new Error('You are not a member of this organization'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  const fallbackOrganization = getFallbackOrg(userOrganizations);
  const organization = params.organization ?? fallbackOrganization;

  if (!organization) {
    return {
      type: 'error',
      error: new Error('No organization found'),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  orgLSData.set(organization);

  if (!params.projectId) {
    // Empty list + failed fetch: 404. Keep a previously loaded list if refetch failed.
    if (!isFetched && projectsError && projects.length === 0) {
      return {
        type: 'error',
        error: projectsError,
        action: 'redirect',
        path: NODON_NOT_FOUND,
      };
    }
    return { type: 'project-list', organization };
  }

  const cachedProject = projects.find(({ id }) => id === params.projectId);
  if (!isFetched && !cachedProject) {
    if (projectsError) {
      return {
        type: 'error',
        error: projectsError,
        action: 'redirect',
        path: NODON_NOT_FOUND,
      };
    }
    return {
      type: 'error',
      error: new Error('Loading projects...'),
      action: 'wait',
    };
  }

  const project = cachedProject;
  if (!project) {
    // Create-and-open (and deep links) can land here before projectsLookup has
    // the row. A version in the URL is enough to open; fetchProject confirms.
    // Without a version, falling back to the list used to look like "create did
    // nothing" — prefer an explicit error so the user gets feedback.
    if (params.versionId) {
      return {
        type: 'project',
        organization,
        projectId: params.projectId,
        versionId: params.versionId,
        page: stringToPage(params.page),
        elementId: params.elementId ?? URL_CATEGORY_NONE,
      };
    }
    return {
      type: 'error',
      error: new Error(PROJECT_NOT_FOUND_MESSAGE),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  const urlVersionId = project.versionIds.find((id) => id === params.versionId);

  const fallbackVersionId = project.versionIds.at(0);
  const activeVersionId = project.active_version_id;

  const versionId = urlVersionId ?? activeVersionId ?? fallbackVersionId;

  if (!versionId) {
    // No version found, redirect to 404
    return {
      type: 'error',
      error: new Error(PROJECT_VERSION_NOT_FOUND_MESSAGE),
      action: 'redirect',
      path: NODON_NOT_FOUND,
    };
  }

  return {
    type: 'project',
    organization,
    projectId: project.id,
    versionId,
    page: stringToPage(params.page),
    elementId: params.elementId ?? URL_CATEGORY_NONE,
  };
};

type NavigationOptions = {
  /** See {@link SearchParamsPatch}. */
  searchParams?: SearchParamsPatch;
  /** Commit the route synchronously when a following mutation would invalidate it. */
  flushSync?: boolean;
};

const NAVIGATION_CANCELLED_LOG: Record<
  'missing-organization' | 'missing-url-parameters',
  string
> = {
  'missing-organization': 'Missing organization, navigation cancelled',
  'missing-url-parameters': 'Missing URL parameters, navigation cancelled',
};

export const useProjectNavigation = () => {
  const reactRouterNavigate = useNavigate();
  const navigate = useCallback(
    (
      path: string,
      options: NavigationOptions = {},
    ): ReturnType<typeof reactRouterNavigate> => {
      // Read search string at invocation time (not from a render-time
      // useSearchParams snapshot) so callers that update query params via
      // setSearchParams immediately before calling navigateTo don't see
      // their just-written params clobbered. window.location.search is the
      // source of truth — react-router's navigate uses history.pushState/
      // replaceState which sync to window.location synchronously.
      let search = window.location.search;
      if (options.searchParams) {
        const params = new URLSearchParams(search);
        for (const [key, value] of Object.entries(options.searchParams)) {
          if (value === null) params.delete(key);
          else params.set(key, value);
        }
        const str = params.toString();
        search = str ? `?${str}` : '';
      }
      const url = path + search;
      reactRouterNavigate(url, { flushSync: options.flushSync });
    },
    [reactRouterNavigate],
  );
  const projectParams = useObjectMemoDeep(useRawProjectParams());

  // Stabilize the returned callback's identity across renders. The body only
  // reads `navigate` and `projectParams` at call time, so we keep both in refs
  // and return a callback with `[]` deps. This prevents downstream consumers
  // (memoized rows, effects that depend on `navigateTo`) from invalidating
  // on every URL/search-param tick.
  const navigateRef = useRef(navigate);
  const projectParamsRef = useRef(projectParams);
  navigateRef.current = navigate;
  projectParamsRef.current = projectParams;

  /**
   * Navigate within the current project context. Omitted {@link navigationParams}
   * keep their current value. Path and `?selected=` come from
   * {@link resolveProjectNavigation}. Pass {@link NavigationOptions.searchParams}
   * to patch the query string in the same navigation.
   */
  return useCallback(
    async (
      navigationParams: Partial<NodonProjectFullUrlParams>,
      options: NavigationOptions = {},
    ) => {
      const navigate = navigateRef.current;
      const projectParams = projectParamsRef.current;
      const result = resolveProjectNavigation({
        route: projectParams,
        navigationParams,
        searchParams: options.searchParams,
      });
      switch (result.action) {
        case 'noop':
          return;
        case 'cancelled':
          console.error(NAVIGATION_CANCELLED_LOG[result.reason]);
          return;
        case 'navigate':
          return navigate(
            result.path,
            innerNavigateOptionsFromProjectNavigate(result, options),
          );
        default:
          return assertUnreachable(result);
      }
    },
    [],
  );
};

/** True when URL has no projectId (project list view). */
export const useIsProjectList = (): boolean => {
  const projectParams = useRawProjectParams();
  return projectParams.type === 'project' && !projectParams.data.projectId;
};

/** True when the active route matches a shared-project URL (incl. legacy links). */
export const useIsSharedProjectRoute = (): boolean =>
  Boolean(useMatch(NODON_SHARED_PROJECT_MATCH_PATH));

/**
 * Return the current page derived directly from the URL (react-router params),
 * without waiting for the URL→UI-store sync effect. Use this when you want
 * `page` to flip in the same render pass as the URL change (e.g. to avoid a
 * wasted pre-flip render of the previous tab).
 */
export const usePageFromUrl = (): Page => {
  const { page } = useParams();
  return stringToPage(page);
};

/** URL `page` for the elements vs products list (not diff/design/etc.). */
export type ElementListUrlPage = Page.Elements | Page.Products;

/**
 * Map a route {@link Page} to the list page used for row selection navigation.
 */
export const elementListUrlPageFromPage = (page: Page): ElementListUrlPage =>
  page === Page.Products ? Page.Products : Page.Elements;

/**
 * Navigate list row selection with `page` kept in sync with the visible list.
 * Reads {@link usePageFromUrl} by default; pass `page` when the tab disagrees
 * with the URL (e.g. proposal actions while `page=products`).
 */
export const useNavigateToElementListSelection = () => {
  const navigateTo = useProjectNavigation();
  const pageFromUrl = usePageFromUrl();

  return useCallback(
    (elementId: string, options?: { page?: ElementListUrlPage }) => {
      const page = options?.page ?? elementListUrlPageFromPage(pageFromUrl);
      void navigateTo({ page, elementId });
    },
    [navigateTo, pageFromUrl],
  );
};

const CLICKABLE_SELECTOR = 'button, [role="button"]';
const FOCUSABLE_INPUT_SELECTOR =
  'input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [contenteditable="true"]';

/**
 * True when {@link element} is a text-entry target that should keep element selection stable.
 */
const isFocusableInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  return element.closest(FOCUSABLE_INPUT_SELECTOR) !== null;
};

/**
 * @param fallbackId - The ID to navigate to if no element is selected.
 * @returns A callback function that deselects the currently selected element and navigates to the fallback ID.
 */
export const useDeselectElement = (fallbackId = URL_CATEGORY_NONE) => {
  const navigateTo = useProjectNavigation();
  const focusedInputBeforePointerDownRef = useRef(false);

  useEffect(() => {
    const captureFocusedInputBeforePointerDown = () => {
      // Capture before the browser moves focus away from the input clicked out of.
      focusedInputBeforePointerDownRef.current = isFocusableInputElement(
        document.activeElement,
      );
    };

    document.addEventListener(
      'pointerdown',
      captureFocusedInputBeforePointerDown,
      true,
    );
    return () =>
      document.removeEventListener(
        'pointerdown',
        captureFocusedInputBeforePointerDown,
        true,
      );
  }, []);

  return useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!is.Element(target)) return;
      if (target.closest(CLICKABLE_SELECTOR)) return;
      // Leaving an input should not also clear the currently selected element.
      if (focusedInputBeforePointerDownRef.current) return;
      const elementId = getSelectedElementId();
      if (elementId && elementId !== URL_CATEGORY_NONE) {
        event.stopPropagation();
        navigateTo({ elementId: fallbackId });
      }
    },
    [navigateTo, fallbackId],
  );
};
